import { invokeEdgeFunction } from './invokeEdgeFunction';

export type InviteMessageTone = 'formal' | 'friendly' | 'conversational' | 'brief';

export type InviteMessageContextUsed = {
  last_meeting_title: string | null;
  last_meeting_date: string | null;
  custom_fields_used: string[];
  prior_invite_sent: boolean;
  prior_invite_sent_at: string | null;
  recent_emails_sent: number;
};

export type GenerateSubscriberInviteMessageResult = {
  subject: string;
  personal_message: string;
  tone: InviteMessageTone;
  context_used: InviteMessageContextUsed;
};

export const INVITE_MESSAGE_TONES: Array<{
  id: InviteMessageTone;
  label: string;
  description: string;
}> = [
  {
    id: 'friendly',
    label: 'Friendly',
    description: 'Warm note — like following up after a good call.',
  },
  {
    id: 'conversational',
    label: 'Conversational',
    description: 'Casual and direct — how you talk on a call.',
  },
  {
    id: 'formal',
    label: 'Formal',
    description: 'Professional and polished.',
  },
  {
    id: 'brief',
    label: 'Brief',
    description: 'Short — a few sentences, straight to the point.',
  },
];

export async function generateSubscriberInviteMessage(
  subscriberId: string,
  tone: InviteMessageTone,
  recipientName?: string | null,
): Promise<GenerateSubscriberInviteMessageResult> {
  return invokeEdgeFunction<GenerateSubscriberInviteMessageResult>(
    'generate-subscriber-invite-message',
    {
      subscriber_id: subscriberId,
      tone,
      recipient_name: recipientName?.trim() || undefined,
    },
  );
}

function formatMeetingDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return null;
  }
}

/** One-line summary of what context the AI used — for the modal hint. */
export function describeInviteMessageContext(ctx: InviteMessageContextUsed): string {
  const parts: string[] = [];
  if (ctx.last_meeting_title) {
    const when = formatMeetingDate(ctx.last_meeting_date);
    parts.push(
      when ? `last call (${when}): ${ctx.last_meeting_title}` : `last call: ${ctx.last_meeting_title}`,
    );
  }
  if (ctx.custom_fields_used.length > 0) {
    parts.push(`profile: ${ctx.custom_fields_used.slice(0, 2).join(', ')}`);
  }
  if (ctx.prior_invite_sent) {
    parts.push('prior invite on file');
  } else if (ctx.recent_emails_sent > 0) {
    parts.push(`${ctx.recent_emails_sent} recent email${ctx.recent_emails_sent === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) return 'Generated from profile — no recent transcript found.';
  return `Used ${parts.join(' · ')}.`;
}
